import {Button, openDeleteDialog, openDialog, Tooltip} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text} from 'platform/foundation';

import {HTMLAttributes} from 'react';

import {DragHandle} from '@dms/features/dnd';
import i18n from '@dms/i18n';

import {DOT_CHARACTER, suffixTestId, TestIdProps} from 'shared/web';

import {KebabMenu} from './KebabMenu';
import {SectionRenameDialog} from './SectionRenameDialog';

export interface SectionHeaderRowProps extends TestIdProps {
  name: string;
  taskCount: number;
  points: number;
  isFixed: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  subtitle?: string;
  onRename?: (name: string) => void;
  onDelete?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onAddTask: () => void;
  handleProps?: HTMLAttributes<unknown>;
}

export function SectionHeaderRow(props: SectionHeaderRowProps) {
  const handleDelete = () =>
    openDeleteDialog({
      text: i18n.t('page.settings.generalChecklist.deleteSectionConfirm', {
        name: props.name,
        count: props.taskCount,
      }),
      onConfirm: () => props.onDelete?.(),
      'data-testid': suffixTestId('sectionDelete', props),
    });

  const handleRename = () =>
    openDialog(
      <SectionRenameDialog
        name={props.name}
        onRename={(name) => props.onRename?.(name)}
        data-testid={suffixTestId('sectionRename', props)}
      />,
      {title: i18n.t('page.settings.generalChecklist.renameSection'), size: 'small'}
    );

  return (
    <Box paddingTop={props.isFixed ? 0 : 4} paddingBottom={2}>
      <HStack spacing={2} align="center">
        <Show when={!props.isFixed}>
          <DragHandle
            {...props.handleProps}
            aria-label={i18n.t('page.settings.generalChecklist.dragSection')}
          >
            <Icon value="action/drag_indicator" size={4} color="text.tertiary" />
          </DragHandle>
        </Show>

        <Heading size={4} data-testid={suffixTestId('sectionName', props)}>
          {props.name}
        </Heading>
        <Show when={Boolean(props.subtitle)}>
          <Tooltip label={props.subtitle}>
            <Icon value="action/info_outline" size={4} color="text.tertiary" />
          </Tooltip>
        </Show>
        <Text size="small" color="tertiary" noWrap>
          {`${i18n.t('page.settings.generalChecklist.taskCount', {
            count: props.taskCount,
          })} ${DOT_CHARACTER} ${i18n.t('page.settings.generalChecklist.pointsCount', {
            count: props.points,
          })}`}
        </Text>

        <Box flex={1} />

        <Button
          variant="link"
          size="small"
          title={i18n.t('entity.settings.labels.addTask')}
          onClick={props.onAddTask}
          data-testid={suffixTestId('addTask', props)}
        />
        <Show when={!props.isFixed}>
          <KebabMenu
            aria-label={i18n.t('page.settings.generalChecklist.sectionActions')}
            data-testid={suffixTestId('sectionMenu', props)}
            items={[
              {
                type: 'item',
                label: i18n.t('general.actions.moveUp'),
                isDisabled: props.isFirst,
                onClick: () => props.onMoveUp?.(),
              },
              {
                type: 'item',
                label: i18n.t('general.actions.moveDown'),
                isDisabled: props.isLast,
                onClick: () => props.onMoveDown?.(),
              },
              {type: 'divider'},
              {type: 'item', label: i18n.t('general.actions.rename'), onClick: handleRename},
              {type: 'divider'},
              {
                type: 'item',
                label: i18n.t('page.settings.generalChecklist.deleteSection'),
                isDanger: true,
                onClick: handleDelete,
              },
            ]}
          />
        </Show>
      </HStack>
    </Box>
  );
}
